import { Box, Button, styled, Typography } from '@mui/material'
import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, unit, UnstyledLink, Z_INDEX } from '@hcr/pwa/components'

import { Path } from '../../../../models'
import { to } from '../../../../utils'
import { BookingList, ButtonBuyAccommodation, WarningMissingDataFloating, LayoutNavigation } from '../../../common'
import { BackgroundImage } from '../background-image'
import { BookingListEmpty } from '../booking-list-empty'
import { BookingListPreview } from '../booking-list-preview'
import { Tile } from '../tile'

interface LayoutHomeProps {
  carousel: ReactNode
  header: ReactNode
  imageUrl: string
  showFallbackWarning?: boolean
}

export const LayoutHome: FC<LayoutHomeProps> = ({ carousel, header, imageUrl, showFallbackWarning = false }) => {
  const { t } = useTranslation()

  return (
    <LayoutNavigation showContextMenuButton>
      {showFallbackWarning && <WarningMissingDataFloating />}
      <Container>
        <BackgroundImage url={imageUrl}>
          <Header>{header}</Header>
        </BackgroundImage>
        <Content>
          <Carousel>{carousel}</Carousel>
          <Tile
            cta={<ButtonBuyAccommodation variant='contained' color='primary' />}
            icon={ICONS.falCalendarHeart}
            title={t('home.your-bookings')}
          >
            <BookingList empty={BookingListEmpty} preview={BookingListPreview} />
          </Tile>
          <Tile
            cta={
              <Button
                component={UnstyledLink}
                to={to(Path.ContextMenuDestinations)}
                variant='contained'
                color='primary'
              >
                {t('home.browse-destinations')}
              </Button>
            }
            icon={ICONS.farSearch}
            title={t('home.browse-destinations')}
          >
            <Typography variant='bodyM' paddingX={unit(5)}>
              {t('home.explore-and-book')}
            </Typography>
          </Tile>
        </Content>
      </Container>
    </LayoutNavigation>
  )
}

const Container = styled(Box)`
  padding: ${unit(3)} ${unit(3)} ${unit(17)};
  background-color: ${COLORS.white};
`

const Header = styled(Box)`
  padding-top: ${unit(80)};
  position: relative;
  z-index: ${Z_INDEX.content};
`

const Content = styled(Box)`
  padding: ${unit(3)} 0;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${unit(4)};
`

const Carousel = styled(Box)`
  margin: auto ${unit(-3)};
  padding: ${unit(5)} ${unit(3)};

  :empty {
    display: none;
  }
`
